/**
 * Fair Value Gap (FVG) Statistics with Placebo Control
 *
 * Port of TradingView's open-source "Fair Value Gap (FVG) Statistics with
 * Placebo Control" by algolego
 * (https://in.tradingview.com/script/Kbq3WncE-Fair-Value-Gap-FVG-Statistics-with-Placebo-Control/).
 *
 * What it does (mirrors the TradingView script):
 * - Detects 3-bar fair value gaps on CLOSED bars:
 *     bull: low[1] > high[3]  (zone = [high[3], low[1]])
 *     bear: high[1] < low[3]  (zone = [low[3], high[1]])
 *   A zone becomes knowable on the bar AFTER the pattern completes —
 *   nothing before that bar is ever counted.
 * - Confirmation-honest sampling: every confirmed zone stays in the sample
 *   from its confirmation bar onward, including zones that fail immediately.
 *   Failed zones are never removed from the statistics.
 * - Scoring (triple-barrier): when price returns to a zone the entry is the
 *   touch bar's close (or the zone edge in the biased mode), with symmetric
 *   target/stop at Barrier size x risk unit (ATR or zone height). Whichever
 *   barrier is hit first wins; a bar touching both is ambiguous (counted as a
 *   loss); nothing resolved within the Time limit is censored (excluded).
 * - MATCHED PLACEBO CONTROL: for every real zone, N placebo zones of the
 *   same height, direction and distance from price are anchored at bars
 *   where nothing happened, then scored by identical rules. The headline
 *   statistic is real minus placebo — not the raw hit rate vs 50%.
 * - Overlap dedupe: same-direction zones overlapping a zone from the last
 *   Overlap lookback bars are flagged (and excluded from the sample) so one
 *   move is not counted as several independent observations.
 * - Optional date-range sample, optional synthetic random-walk validation
 *   mode (both hit rates must bracket 50% on a driftless walk).
 * - Drawn zones (TV style): solid boxes extending right while open; frozen
 *   and faded to a dotted outline once price returns to them. Rendered via
 *   IndicatorBand (toTime bounds touched boxes, dotted marks the fade).
 * - Emits dashboard metadata for the top-right "FVG STATISTICS" table
 *   overlay — same rows as TradingView (compact panel by default).
 *
 * Known platform differences (documented in the TV script as well):
 * - Date range timestamps are interpreted as UTC midnight (Pine uses the
 *   chart's exchange timezone).
 * - Synthetic mode uses our own seeded PRNG (TV's math.random stream
 *   cannot be reproduced); validation logic is otherwise identical.
 * - The chart timeframe shown in the panel header is derived from the
 *   candle spacing (Pine reads timeframe.period directly).
 *
 * Category: Advanced | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorBand } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FvgZone {
  isBull: boolean;
  isReal: boolean;
  isDup: boolean;
  confBar: number;
  top: number;
  bot: number;
  rUnit: number;
  touched: boolean;
  entry: number;
  target: number;
  stopP: number;
  entryBar: number;
  mfe: number;
  mae: number;
  /** 0 pending, 1 target, 2 stop, 3 ambiguous, 4 censored */
  outcome: 0 | 1 | 2 | 3 | 4;
}

interface FvgPending {
  dueBar: number;
  isBull: boolean;
  heightAtr: number;
  standoffAtr: number;
}

interface FvgStats {
  nConf: number;
  nTouch: number;
  nTarget: number;
  nStop: number;
  nAmbig: number;
  nCens: number;
  sumMfe: number;
  sumMae: number;
  resBars: number[];
}

/** One row of the top-right "FVG STATISTICS" table */
export interface FvgRow {
  k: string;
  v: string;
  /** value color: up/down/warn follow TV palette, val = theme foreground,
   *  txt = muted label color */
  c: "up" | "down" | "warn" | "val" | "txt";
}

/** Dashboard metadata for the overlay component */
export interface FvgDashboard {
  rows: FvgRow[];
}

const COL_UP = "up";
const COL_DOWN = "down";
const COL_WARN = "warn";
const COL_VAL = "val";
const COL_TXT = "txt";

const BULL_HEX = "#26A69A";
const BEAR_HEX = "#EF5350";

// ─── Formatting helpers (Pine str.tostring conventions) ──────────────────────

/** Pine `str.tostring(v * 100, "#.#") + "%"`, "--" when na */
export const fvgPct = (v: number | null): string => {
  if (v === null || v === undefined || !isFinite(v)) return "--";
  let s = (v * 100).toFixed(1);
  if (s.endsWith(".0")) s = s.slice(0, -2);
  return s + "%";
};

/** Pine `str.tostring(v, "#.##")`, "--" when na */
export const fvgNum = (v: number | null): string => {
  if (v === null || v === undefined || !isFinite(v)) return "--";
  let s = v.toFixed(2);
  if (s.endsWith("0")) s = s.slice(0, -1);
  if (s.endsWith("0")) s = s.slice(0, -1);
  if (s.endsWith(".")) s = s.slice(0, -1);
  return s;
};

/** Pine `str.tostring(v, "#.#")` (used for kR and median bars) */
const fmt1 = (v: number): string => {
  let s = v.toFixed(1);
  if (s.endsWith(".0")) s = s.slice(0, -2);
  return s;
};

/** Pine `str.format_time(t, "yyyy-MM-dd")` — UTC (see header note) */
const fvgDate = (t: number | null): string => {
  if (t === null || t === undefined || !isFinite(t)) return "--";
  const d = new Date(t * 1000);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
};

/** hex + alpha (0..1) -> css rgba() */
const rgba = (hex: string, alpha: number): string => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/** Median of an array (mean of the two middle values when even-sized) */
const median = (arr: number[]): number | null => {
  if (arr.length === 0) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/** Deterministic PRNG (mulberry32) for synthetic validation mode */
const mulberry32 = (seed: number) => {
  let a = seed >>> 0;
  return (): number => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// ─── Statistics (identical formulas to the Pine script) ──────────────────────

const hitRate = (s: FvgStats): number | null => {
  const d = s.nTarget + s.nStop + s.nAmbig;
  return d > 0 ? s.nTarget / d : null;
};

const hr2 = (x: number, n: number): number | null => (n > 0 ? x / n : null);

/** Wilson score interval at z = 1.96 */
const wilson = (x: number, n: number): [number | null, number | null] => {
  if (n <= 0) return [null, null];
  const zc = 1.96;
  const p = x / n;
  const c = (p + (zc * zc) / (2 * n)) / (1 + (zc * zc) / n);
  const h =
    (zc / (1 + (zc * zc) / n)) *
    Math.sqrt((p * (1 - p)) / n + (zc * zc) / (4 * n * n));
  return [c - h, c + h];
};

/** Two-proportion z-test (pooled) */
const twoProp = (
  x1: number, n1: number, x2: number, n2: number
): number | null => {
  if (n1 <= 0 || n2 <= 0) return null;
  const p = (x1 + x2) / (n1 + n2);
  const se = Math.sqrt(p * (1 - p) * (1 / n1 + 1 / n2));
  return se > 0 ? (x1 / n1 - x2 / n2) / se : null;
};

const newStats = (): FvgStats => ({
  nConf: 0,
  nTouch: 0,
  nTarget: 0,
  nStop: 0,
  nAmbig: 0,
  nCens: 0,
  sumMfe: 0,
  sumMae: 0,
  resBars: [],
});

/** Pine `timeframe.period` approximation from candle spacing */
const timeframeLabel = (candles: CandleData[]): string => {
  if (candles.length < 2) return "--";
  const deltas: number[] = [];
  for (let i = 1; i < Math.min(candles.length, 50); i++) {
    deltas.push(candles[i].time - candles[i - 1].time);
  }
  deltas.sort((a, b) => a - b);
  const sec = deltas[deltas.length >> 1];
  if (sec <= 0) return "--";
  if (sec >= 86400 * 28 && sec <= 86400 * 31) return "M";
  if (sec >= 86400 * 6 && sec <= 86400 * 8) return "W";
  if (sec >= 86400) return "D";
  if (sec % 60 === 0) return String(sec / 60);
  return String(sec) + "S";
};

// ─── Indicator definition ────────────────────────────────────────────────────

export const fvgStatisticsIndicator: IndicatorDefinition = {
  id: "fvg_statistics_placebo",
  name: "Fair Value Gap (FVG) Statistics with Placebo Control",
  shortName: "FVG Stats",
  category: "advanced",
  pane: "overlay",
  params: [
    // Measurement
    {
      key: "rMode", label: "Risk unit (R)", type: "select", default: "ATR",
      group: "Measurement",
      options: [{ label: "ATR", value: "ATR" }, { label: "Zone height", value: "Zone height" }],
    },
    { key: "atrLen", label: "ATR length", type: "number", default: 14, min: 1, max: 500, group: "Measurement" },
    { key: "kR", label: "Barrier size (R)", type: "number", default: 2.0, min: 0.1, max: 10, step: 0.1, group: "Measurement" },
    { key: "maxBars", label: "Time limit after entry (bars)", type: "number", default: 100, min: 1, max: 2000, group: "Measurement" },
    {
      key: "entryMode", label: "Entry price", type: "select", default: "Bar close",
      group: "Measurement",
      options: [{ label: "Bar close", value: "Bar close" }, { label: "Zone edge (biased)", value: "Zone edge (biased)" }],
    },
    { key: "entryBarExits", label: "Evaluate exits on the entry bar (biased)", type: "boolean", default: false, group: "Measurement" },
    { key: "minHgt", label: "Minimum zone height (ATR)", type: "number", default: 0.0, min: 0, max: 10, step: 0.1, group: "Measurement" },
    { key: "dedupe", label: "Exclude overlapping same-direction zones", type: "boolean", default: true, group: "Measurement" },
    { key: "dupWin", label: "Overlap lookback (bars)", type: "number", default: 20, min: 1, max: 2000, group: "Measurement" },
    // Sample
    { key: "useRange", label: "Limit to date range", type: "boolean", default: false, group: "Sample" },
    { key: "startY", label: "Start Year", type: "number", default: 2025, min: 1970, max: 2100, group: "Sample" },
    { key: "startM", label: "Start Month", type: "number", default: 1, min: 1, max: 12, group: "Sample" },
    { key: "startD", label: "Start Day", type: "number", default: 1, min: 1, max: 31, group: "Sample" },
    { key: "endY", label: "End Year", type: "number", default: 2026, min: 1970, max: 2100, group: "Sample" },
    { key: "endM", label: "End Month", type: "number", default: 8, min: 1, max: 12, group: "Sample" },
    { key: "endD", label: "End Day", type: "number", default: 1, min: 1, max: 31, group: "Sample" },
    // Placebo control
    { key: "useNull", label: "Run placebo control", type: "boolean", default: true, group: "Placebo control" },
    { key: "nPlac", label: "Placebos per real zone", type: "number", default: 3, min: 1, max: 10, group: "Placebo control" },
    { key: "offBase", label: "Placebo offset (bars)", type: "number", default: 211, min: 10, max: 10000, group: "Placebo control" },
    // Validation (synthetic data)
    { key: "synth", label: "Replace price with a random walk", type: "boolean", default: false, group: "Validation (synthetic data)" },
    { key: "synSeed", label: "Seed", type: "number", default: 12345, min: 0, max: 999999, group: "Validation (synthetic data)" },
    { key: "synVol", label: "Sub-step size", type: "number", default: 1.0, min: 0.01, max: 100, step: 0.1, group: "Validation (synthetic data)" },
    { key: "synDrift", label: "Drift per bar (0 = driftless null)", type: "number", default: 0.0, min: -100, max: 100, step: 0.05, group: "Validation (synthetic data)" },
    // Display
    { key: "compact", label: "Compact panel", type: "boolean", default: true, group: "Display" },
    { key: "showZones", label: "Draw real zones", type: "boolean", default: true, group: "Display" },
  ],
  plots: [
    { key: "zones", label: "FVG Zones", color: "#26A69A", style: "line", lastValueVisible: false, priceLineVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const {
      rMode, atrLen, kR, maxBars, entryMode, entryBarExits, minHgt,
      dedupe, dupWin, useRange, startY, startM, startD, endY, endM, endD,
      useNull, nPlac, offBase, synth, synSeed, synVol, synDrift,
      compact, showZones,
    } = params;

    const n = candles.length;

    // ── Routed series (synthetic replaces price) ─────────────────────────────
    // Four sub-steps per bar give the synthetic bars a real intrabar path.
    const H = new Array<number>(n);
    const L = new Array<number>(n);
    const C = new Array<number>(n);
    if (synth && n > 0) {
      const rand = mulberry32(Math.round(synSeed) >>> 0);
      const d4 = Number(synDrift) / 4;
      const vol = Number(synVol);
      let prev = 10000.0;
      for (let i = 0; i < n; i++) {
        const r1 = rand() * 2 - 1;
        const r2 = rand() * 2 - 1;
        const r3 = rand() * 2 - 1;
        const r4 = rand() * 2 - 1;
        const sOpen = prev;
        const sp1 = sOpen + r1 * vol + d4;
        const sp2 = sp1 + r2 * vol + d4;
        const sp3 = sp2 + r3 * vol + d4;
        const sClose = sp3 + r4 * vol + d4;
        H[i] = Math.max(sOpen, sp1, sp2, sp3, sClose);
        L[i] = Math.min(sOpen, sp1, sp2, sp3, sClose);
        C[i] = sClose;
        prev = sClose;
      }
    } else {
      for (let i = 0; i < n; i++) {
        H[i] = candles[i].high;
        L[i] = candles[i].low;
        C[i] = candles[i].close;
      }
    }

    // ── ATR (RMA of True Range on the routed series) ─────────────────────────
    const period = Math.max(1, Math.round(atrLen));
    const atr = new Array<number>(n).fill(NaN);
    if (n >= period) {
      const tr: number[] = new Array(n);
      tr[0] = H[0] - L[0];
      for (let i = 1; i < n; i++) {
        tr[i] = Math.max(
          H[i] - L[i],
          Math.abs(H[i] - C[i - 1]),
          Math.abs(L[i] - C[i - 1])
        );
      }
      let a = 0;
      for (let i = 0; i < period; i++) a += tr[i];
      a /= period;
      atr[period - 1] = a;
      for (let i = period; i < n; i++) {
        a = (a * (period - 1) + tr[i]) / period;
        atr[i] = a;
      }
    }

    // ── Sample range ─────────────────────────────────────────────────────────
    const startT = Date.UTC(Math.round(startY), Math.round(startM) - 1, Math.round(startD)) / 1000;
    const endT = Date.UTC(Math.round(endY), Math.round(endM) - 1, Math.round(endD)) / 1000;
    const inRangeAt = (i: number): boolean =>
      !useRange || (candles[i].time >= startT && candles[i].time <= endT);

    const edgeMode = entryMode === "Zone edge (biased)";

    // ── State (Pine `var` arrays) ────────────────────────────────────────────
    const active: FvgZone[] = [];
    const recentZ: FvgZone[] = [];   // overlap cache, real zones
    const recentP: FvgZone[] = [];   // overlap cache, placebos
    const queued: FvgPending[] = [];
    const drawnZones: FvgZone[] = []; // zones eligible for a drawn box (cap 400)

    const sReal = newStats();
    const sPlac = newStats();
    let nConfRaw = 0;
    let nDup = 0;
    let firstT: number | null = null;
    let lastT: number | null = null;
    let nBarsAll = 0;
    let nBarsRange = 0;
    let chartFirstT: number | null = null;

    let rBullT = 0, rBullD = 0, rBearT = 0, rBearD = 0;
    let pBullT = 0, pBullD = 0, pBearT = 0, pBearD = 0;

    const overlapsIn = (arr: FvgZone[], t: number, b: number, isBull: boolean): boolean =>
      arr.some(z => z.isBull === isBull && t >= z.bot && b <= z.top);

    const pruneTo = (arr: FvgZone[], i: number, win: number): void => {
      for (let k = arr.length - 1; k >= 0; k--) {
        if (i - arr[k].confBar > win) arr.splice(k, 1);
      }
    };

    const makeZone = (isBull: boolean, t: number, b: number, isReal: boolean, i: number): FvgZone => ({
      isBull,
      isReal,
      isDup: false,
      confBar: i,
      top: t,
      bot: b,
      rUnit: rMode === "ATR" ? atr[i] : t - b,
      touched: false,
      entry: NaN,
      target: NaN,
      stopP: NaN,
      entryBar: NaN,
      mfe: 0,
      mae: 0,
      outcome: 0,
    });

    // ── Main loop ────────────────────────────────────────────────────────────
    for (let i = 0; i < n; i++) {
      nBarsAll++;
      if (i === 0) chartFirstT = candles[0].time;
      const inRange = inRangeAt(i);
      if (inRange) {
        if (firstT === null) firstT = candles[i].time;
        lastT = candles[i].time;
        nBarsRange++;
      }

      // 1. Detection — 3-bar FVG pattern completes on bar [1], zone becomes
      //    knowable NOW. Nothing before this bar is ever counted.
      const bullFvg = i >= 3 && L[i - 1] > H[i - 3];
      const bearFvg = i >= 3 && H[i - 1] < L[i - 3];
      const a = atr[i];
      if ((bullFvg || bearFvg) && isFinite(a) && a > 0 && inRange) {
        const isBull = bullFvg;
        const t = isBull ? L[i - 1] : L[i - 3];
        const b = isBull ? H[i - 3] : H[i - 1];
        if ((t - b) / a >= Number(minHgt)) {
          pruneTo(recentZ, i, Number(dupWin));
          const z = makeZone(isBull, t, b, true, i);
          z.isDup = Boolean(dedupe) && overlapsIn(recentZ, t, b, isBull);
          active.push(z);
          recentZ.push(z);
          nConfRaw++;
          if (z.isDup) {
            nDup++;
          } else {
            sReal.nConf++;
            // Drawn box: solid, extends right while open (cap 400 by age)
            drawnZones.push(z);
            if (drawnZones.length > 400) drawnZones.shift();
          }

          // Matched placebos: same height, standoff and direction, anchored
          // at bars where no structural event occurred.
          if (useNull && !z.isDup) {
            const proximal = isBull ? t : b;
            const standoff = Math.abs(C[i] - proximal) / a;
            const height = (t - b) / a;
            for (let k = 1; k <= Math.round(nPlac); k++) {
              queued.push({
                dueBar: i + Math.round(offBase) * k,
                isBull,
                heightAtr: height,
                standoffAtr: standoff,
              });
            }
          }
        }
      }

      // 2. Placebo activation — anchored on the PREVIOUS bar's close so the
      //    zone is fully defined before the bar we test for a touch.
      if (queued.length > 0 && i >= 1 && isFinite(atr[i - 1]) && atr[i - 1] > 0) {
        for (let qi = queued.length - 1; qi >= 0; qi--) {
          const p = queued[qi];
          if (i >= p.dueBar) {
            const h = p.heightAtr * atr[i - 1];
            const so = p.standoffAtr * atr[i - 1];
            let t = NaN;
            let b = NaN;
            if (p.isBull) {
              t = C[i - 1] - so;
              b = t - h;
            } else {
              b = C[i - 1] + so;
              t = b + h;
            }
            if (h > 0) {
              pruneTo(recentP, i, Number(dupWin));
              if (!(dedupe && overlapsIn(recentP, t, b, p.isBull))) {
                const pz = makeZone(p.isBull, t, b, false, i);
                active.push(pz);
                recentP.push(pz);
                sPlac.nConf++;
              }
            }
            queued.splice(qi, 1);
          }
        }
      }

      // 3. Evaluation — touch detection, then the barrier race from the bar
      //    AFTER entry (OHLC cannot order events inside the entry bar).
      for (let ai = active.length - 1; ai >= 0; ai--) {
        const z = active[ai];
        let justEntered = false;

        if (!z.touched) {
          const prox = z.isBull ? z.top : z.bot;
          const hit = z.isBull ? L[i] <= prox : H[i] >= prox;
          if (hit) {
            z.touched = true;
            z.entry = edgeMode ? prox : C[i];
            z.entryBar = i;
            const r = z.rUnit * Number(kR);
            z.target = z.isBull ? z.entry + r : z.entry - r;
            z.stopP = z.isBull ? z.entry - r : z.entry + r;
            justEntered = true;
          }
        }

        if (
          z.touched &&
          z.outcome === 0 &&
          (!justEntered || (Boolean(entryBarExits) && edgeMode))
        ) {
          const r = z.rUnit * Number(kR);
          z.mfe = Math.max(z.mfe, (z.isBull ? H[i] - z.entry : z.entry - L[i]) / r);
          z.mae = Math.max(z.mae, (z.isBull ? z.entry - L[i] : H[i] - z.entry) / r);

          const hitT = z.isBull ? H[i] >= z.target : L[i] <= z.target;
          const hitS = z.isBull ? L[i] <= z.stopP : H[i] >= z.stopP;

          z.outcome =
            hitT && hitS ? 3 :
            hitT ? 1 :
            hitS ? 2 :
            i - z.entryBar >= Number(maxBars) ? 4 : 0;

          if (z.outcome !== 0) {
            const won = z.outcome === 1;
            const resolved = z.outcome !== 4;
            if (z.isReal && !z.isDup) {
              sReal.nTouch++;
              sReal.sumMfe += z.mfe;
              sReal.sumMae += z.mae;
              if (z.outcome === 1) sReal.nTarget++;
              else if (z.outcome === 2) sReal.nStop++;
              else if (z.outcome === 3) sReal.nAmbig++;
              else if (z.outcome === 4) sReal.nCens++;
              if (resolved) sReal.resBars.push(i - z.entryBar);
              if (resolved) {
                if (z.isBull) { rBullD++; if (won) rBullT++; }
                else { rBearD++; if (won) rBearT++; }
              }
            } else if (!z.isReal) {
              sPlac.nTouch++;
              sPlac.sumMfe += z.mfe;
              sPlac.sumMae += z.mae;
              if (z.outcome === 1) sPlac.nTarget++;
              else if (z.outcome === 2) sPlac.nStop++;
              else if (z.outcome === 3) sPlac.nAmbig++;
              else if (z.outcome === 4) sPlac.nCens++;
              if (resolved) sPlac.resBars.push(i - z.entryBar);
              if (resolved) {
                if (z.isBull) { pBullD++; if (won) pBullT++; }
                else { pBearD++; if (won) pBearT++; }
              }
            }
            active.splice(ai, 1);
          }
        }
      }
    }

    // ── Readout (Pine `barstate.islast`) ─────────────────────────────────────
    const dR = sReal.nTarget + sReal.nStop + sReal.nAmbig;
    const dP = sPlac.nTarget + sPlac.nStop + sPlac.nAmbig;
    const hr = hitRate(sReal);
    const hrP = hitRate(sPlac);
    const [lo, hi] = wilson(sReal.nTarget, dR);
    const [plo, phi] = wilson(sPlac.nTarget, dP);
    const zScore = dR > 0 && hr !== null ? (hr - 0.5) * 2 * Math.sqrt(dR) : null;
    const medBars = median(sReal.resBars);
    const touchPc = sReal.nConf > 0 ? sReal.nTouch / sReal.nConf : null;
    const degen = medBars !== null && medBars <= 2;

    const dlt = hr !== null && hrP !== null ? hr - hrP : null;
    const zd = twoProp(sReal.nTarget, dR, sPlac.nTarget, dP);

    // On a driftless walk both intervals must contain 50%
    const selfPass =
      lo !== null && hi !== null && plo !== null && phi !== null &&
      lo <= 0.5 && hi >= 0.5 && plo <= 0.5 && phi >= 0.5;
    const hdr = synth
      ? Number(synDrift) !== 0
        ? "DRIFT TEST"
        : selfPass ? "VALIDATION PASS" : "VALIDATION FAIL"
      : timeframeLabel(candles);
    const hdrC: FvgRow["c"] = !synth
      ? COL_VAL
      : Number(synDrift) !== 0 ? COL_WARN : selfPass ? COL_UP : COL_DOWN;

    const empty = Boolean(useRange) && nBarsRange === 0;

    // Row queue (identical order to the Pine table)
    const rows: FvgRow[] = [];
    const addRow = (k: string, v: string, c: FvgRow["c"]) => rows.push({ k, v, c });

    addRow("FVG STATISTICS", hdr, hdrC);
    addRow(
      synth ? `SYNTHETIC  seed ${Math.round(synSeed)}` : "Sample",
      synth
        ? `drift ${fvgNum(Number(synDrift))}`
        : empty ? "NO BARS IN RANGE" : `${fvgDate(firstT)}  ${fvgDate(lastT)}`,
      synth ? COL_WARN : empty ? COL_DOWN : COL_VAL
    );

    if (!compact) {
      addRow("Range set", useRange ? `${fvgDate(startT)}  ${fvgDate(endT)}` : "off",
        useRange ? COL_VAL : COL_TXT);
      addRow("Chart loaded", `${fvgDate(chartFirstT)}   ${nBarsAll}b`, COL_VAL);
      addRow("Bars in range", String(nBarsRange), empty ? COL_DOWN : COL_VAL);
    }

    addRow(
      "Confirmed zones",
      `${nConfRaw}` + (dedupe ? `  (-${nDup} overlap)` : ""),
      COL_VAL
    );
    addRow("Returned to zone", `${sReal.nTouch}  ${fvgPct(touchPc)}`, COL_VAL);

    if (!compact) {
      addRow("  -> target first", String(sReal.nTarget), COL_UP);
      addRow("  -> stop first", String(sReal.nStop), COL_DOWN);
      addRow("  -> ambiguous bar", String(sReal.nAmbig), COL_WARN);
      addRow("  -> unresolved", String(sReal.nCens), COL_VAL);
    }

    addRow(`Hit rate (n=${dR})`, fvgPct(hr),
      hr === null ? COL_VAL : hr > 0.5 ? COL_UP : COL_DOWN);
    addRow("  95% interval",
      lo === null || hi === null ? "--" : `${fvgPct(lo)} - ${fvgPct(hi)}`, COL_VAL);

    if (!compact) {
      addRow("  z vs coinflip", fvgNum(zScore), COL_VAL);
      addRow("  bull / bear",
        `${fvgPct(hr2(rBullT, rBullD))} / ${fvgPct(hr2(rBearT, rBearD))}   ${rBullD}/${rBearD}`,
        COL_VAL);
    }

    addRow(`Placebo (n=${dP})`, fvgPct(hrP), COL_VAL);

    if (!compact) {
      addRow("  95% interval",
        plo === null || phi === null ? "--" : `${fvgPct(plo)} - ${fvgPct(phi)}`, COL_VAL);
      addRow("  bull / bear",
        `${fvgPct(hr2(pBullT, pBullD))} / ${fvgPct(hr2(pBearT, pBearD))}   ${pBullD}/${pBearD}`,
        COL_VAL);
      addRow("  ambiguous r / p",
        `${fvgPct(hr2(sReal.nAmbig, dR))} / ${fvgPct(hr2(sPlac.nAmbig, dP))}`, COL_WARN);
      addRow("  unresolved r / p",
        `${fvgPct(hr2(sReal.nCens, dR + sReal.nCens))} / ${fvgPct(hr2(sPlac.nCens, dP + sPlac.nCens))}`,
        COL_WARN);
    }

    addRow("real - placebo",
      dlt === null ? "--" : `${fvgPct(dlt)}   z ${fvgNum(zd)}`,
      dlt === null ? COL_VAL : dlt > 0 ? COL_UP : COL_DOWN);
    addRow("Entry / barrier",
      `${edgeMode ? "edge (biased)" : "close"} / ${fmt1(Number(kR))}R  ` +
      (medBars === null ? "--" : `${fmt1(medBars)}b`) +
      (degen ? "  TOO TIGHT" : ""),
      edgeMode || degen ? COL_WARN : COL_VAL);

    // ── Drawn zone bands ─────────────────────────────────────────────────────
    const bands: IndicatorBand[] = [];
    if (showZones && !synth) {
      drawnZones.forEach((z, idx) => {
        const col = z.isBull ? BULL_HEX : BEAR_HEX;
        // TV: open zones solid (border 10% transp., fill 70%); once touched
        // the box freezes and fades (border 45% transp., fill 85%) + dotted.
        bands.push({
          id: `fvg_${idx}`,
          top: z.top,
          bottom: z.bot,
          fill: rgba(col, z.touched ? 0.15 : 0.3),
          border: rgba(col, z.touched ? 0.55 : 0.9),
          fromTime: candles[z.confBar].time,
          toTime: z.touched
            ? z.entryBar + 1 < n
              ? candles[Math.round(z.entryBar) + 1].time
              : undefined
            : undefined,
          dotted: z.touched,
          lineWidth: 1,
        });
      });
    }

    const dashboard: FvgDashboard = { rows };

    return [
      {
        key: "zones",
        data: [],
        bands,
        metadata: {
          dashboard,
          summary: {
            confirmed: nConfRaw,
            duplicates: nDup,
            touched: sReal.nTouch,
            resolvedReal: dR,
            resolvedPlacebo: dP,
            hitRate: hr,
            hitRatePlacebo: hrP,
            realMinusPlacebo: dlt,
            target: sReal.nTarget,
            stop: sReal.nStop,
            ambiguous: sReal.nAmbig,
            censored: sReal.nCens,
          },
        },
      },
    ];
  },
};
